(() => {
  if (customElements.get('x-belt-configurator')) return;

  const stateStore = (window.XEngineersXbeltState = window.XEngineersXbeltState || {});

  class XEngineersXbeltConfigurator extends HTMLElement {
    connectedCallback() {
      this.variantSelects = this.closest('variant-selects');
      this.productInfo = this.closest('product-info');
      this.productFormElement = this.productInfo?.querySelector('product-form');
      this.productForm = this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
      this.quantityInput = this.productInfo?.querySelector('.quantity__input');
      this.productData = this.parseJson('[data-xbelt-product-json]');
      this.addonData = this.parseAddonData();
      this.nativeOptionGroups = Array.from(this.querySelectorAll('fieldset[data-xbelt-native-position]'));

      if (!this.productData || !this.addonData || !this.nativeOptionGroups.length) return;

      this.nativeStyleGroup = this.detectNativeStyleGroup();
      this.styleOptionIndex = Math.max(0, Number(this.nativeStyleGroup?.dataset.xbeltNativePosition || 1) - 1);
      this.classicNativeValue = this.resolveNativeStyleValue('classic');
      this.badgeNativeValue = this.resolveNativeStyleValue('badge');
      this.legacyVariantMode = !this.badgeNativeValue;

      if (!this.nativeStyleGroup || !this.classicNativeValue) return;

      this.nativeStyleGroup.hidden = true;
      this.nativeStyleGroup.dataset.xbeltNativeOption = 'style';

      this.handleChangeBound = this.handleChange.bind(this);
      this.handleQuantityBound = this.handleQuantityChange.bind(this);
      this.addEventListener('change', this.handleChangeBound);
      this.quantityInput?.addEventListener('input', this.handleQuantityBound);
      this.quantityInput?.addEventListener('change', this.handleQuantityBound);

      this.initializeState();
      this.ensureValidState();
      this.saveState();
      this.syncNativeVariant();
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChangeBound);
      this.quantityInput?.removeEventListener('input', this.handleQuantityBound);
      this.quantityInput?.removeEventListener('change', this.handleQuantityBound);
    }

    parseJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || 'null');
      } catch (error) {
        console.error('X-Belt configurator data could not be read.', error);
        return null;
      }
    }

    parseAddonData() {
      const serializedData = this.parseJson('[data-xbelt-addon-json]');
      if (Array.isArray(serializedData?.variants)) return serializedData;

      const variants = Array.from(this.querySelectorAll('[data-xbelt-badge]'))
        .map((input) => {
          const card = input.closest('[data-xbelt-badge-card]');
          return {
            id: input.dataset.addonVariantId,
            title: input.value,
            available: card?.dataset.addonAvailable === 'true',
            price: Number(input.dataset.addonPrice || 0),
          };
        })
        .filter((variant) => variant.id && variant.title);

      return variants.length ? { variants } : null;
    }

    canonical(value = '') {
      return String(value)
        .toLocaleLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
        .replace(/badge$/, '');
    }

    valuesForGroup(group) {
      if (!group) return [];
      const radioValues = Array.from(group.querySelectorAll('input[type="radio"]')).map((input) => input.value);
      const selectValues = Array.from(group.querySelectorAll('select option')).map((option) => option.value);
      return [...new Set([...radioValues, ...selectValues].filter(Boolean))];
    }

    isClassicValue(value) {
      const key = this.canonical(value);
      const configuredKey = this.canonical(this.dataset.classicValue);
      return key === configuredKey || key.includes('classic') || key.includes('nobadge');
    }

    isBadgeBaseValue(value) {
      const key = this.canonical(value);
      const configuredKey = this.canonical(this.dataset.badgeValue);
      if (key === configuredKey) return true;
      return key.includes('badge') && key.includes('belt') && !this.isClassicValue(value);
    }

    detectNativeStyleGroup() {
      let bestGroup = null;
      let bestScore = -1;

      this.nativeOptionGroups.forEach((group) => {
        const values = this.valuesForGroup(group);
        const hasClassic = values.some((value) => this.isClassicValue(value));
        const hasBadgeBase = values.some((value) => this.isBadgeBaseValue(value));
        const hasLegacyBadge = values.some((value) => Boolean(this.addonVariantFor(value)));
        let score = group.dataset.xbeltNativeOption === 'style' ? 10 : 0;

        if (hasClassic && hasLegacyBadge) score = Math.max(score, 20);
        if (hasClassic && hasBadgeBase) score = Math.max(score, 30);

        if (score > bestScore) {
          bestScore = score;
          bestGroup = group;
        }
      });

      return bestGroup;
    }

    resolveNativeStyleValue(style) {
      const values = this.valuesForGroup(this.nativeStyleGroup);
      const configuredValue = style === 'classic' ? this.dataset.classicValue : this.dataset.badgeValue;
      const configuredKey = this.canonical(configuredValue);
      const exactValue = values.find((value) => this.canonical(value) === configuredKey);
      if (exactValue) return exactValue;

      return style === 'classic'
        ? values.find((value) => this.isClassicValue(value)) || null
        : values.find((value) => this.isBadgeBaseValue(value)) || null;
    }

    initializeState() {
      const saved = stateStore[this.dataset.stateKey];
      const checkedNativeValue = this.selectedValueForGroup(this.nativeStyleGroup);
      const checkedBadgeTitle = this.legacyVariantMode
        ? this.addonTitleFor(checkedNativeValue) || checkedNativeValue
        : null;
      const isClassic = !checkedNativeValue || this.isClassicValue(checkedNativeValue);

      if (saved) {
        this.state = {
          style: saved.style === 'badge' ? 'badge' : 'classic',
          badges: Array.isArray(saved.badges) ? [...saved.badges] : [],
        };
      } else {
        this.state = {
          style: isClassic ? 'classic' : 'badge',
          badges: !isClassic && checkedBadgeTitle ? [checkedBadgeTitle] : [],
        };
      }
    }

    saveState() {
      stateStore[this.dataset.stateKey] = {
        style: this.state.style,
        badges: [...this.state.badges],
      };
    }

    get nativeStyleInputs() {
      return Array.from(this.nativeStyleGroup?.querySelectorAll('input[type="radio"]') || []);
    }

    get badgeInputs() {
      return Array.from(this.querySelectorAll('[data-xbelt-badge]'));
    }

    get badgeTitles() {
      return this.badgeInputs.map((input) => input.value);
    }

    addonVariantFor(title) {
      const key = this.canonical(title);
      return this.addonData?.variants?.find((variant) => this.canonical(variant.title) === key);
    }

    addonTitleFor(title) {
      return this.addonVariantFor(title)?.title;
    }

    selectedValueForGroup(group) {
      const selectedInput = group?.querySelector('input[type="radio"]:checked');
      const selectedOption = group?.querySelector('select option:checked');
      return selectedInput?.value || selectedOption?.value || null;
    }

    selectedNativeValues() {
      return this.productData.options.map((_optionName, index) => {
        const position = index + 1;
        const group = this.nativeOptionGroups.find(
          (candidate) => Number(candidate.dataset.xbeltNativePosition) === position
        );
        return this.selectedValueForGroup(group);
      });
    }

    nativeInputForValue(value) {
      const key = this.canonical(value);
      return this.nativeStyleInputs.find((input) => this.canonical(input.value) === key);
    }

    setNativeStyleValue(value) {
      const nativeInput = this.nativeInputForValue(value);
      if (nativeInput) {
        if (nativeInput.checked) return false;
        nativeInput.checked = true;
        nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const select = this.nativeStyleGroup?.querySelector('select');
      const matchingOption = Array.from(select?.options || []).find(
        (option) => this.canonical(option.value) === this.canonical(value)
      );
      if (!select || !matchingOption || select.value === matchingOption.value) return false;
      select.value = matchingOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    targetNativeValue(style, primaryBadgeTitle) {
      if (style === 'classic') return this.classicNativeValue;
      return this.legacyVariantMode ? primaryBadgeTitle : this.badgeNativeValue;
    }

    baseVariantForStyle(style, primaryBadgeTitle = null) {
      const targetValue = this.targetNativeValue(style, primaryBadgeTitle);
      if (!targetValue) return null;

      const selectedValues = this.selectedNativeValues();
      selectedValues[this.styleOptionIndex] = targetValue;
      const styleKey = this.canonical(targetValue);

      return this.productData.variants.find((variant) =>
        variant.options.every((value, index) => {
          if (index === this.styleOptionIndex) return this.canonical(value) === styleKey;
          return selectedValues[index] == null || String(value) === String(selectedValues[index]);
        })
      );
    }

    classicVariant() {
      return this.baseVariantForStyle('classic');
    }

    badgeVariant(title = this.state?.badges?.[0]) {
      return this.baseVariantForStyle('badge', title);
    }

    primaryBadgeAvailable(title) {
      const baseVariant = this.badgeVariant(title);
      if (!baseVariant?.available) return false;
      if (this.legacyVariantMode) return true;
      return Boolean(this.addonVariantFor(title)?.available);
    }

    ensureValidState() {
      const knownTitles = new Map(this.badgeTitles.map((title) => [this.canonical(title), title]));
      const deduplicated = [];

      for (const title of this.state.badges) {
        const knownTitle = knownTitles.get(this.canonical(title));
        if (knownTitle && !deduplicated.some((item) => this.canonical(item) === this.canonical(knownTitle))) {
          deduplicated.push(knownTitle);
        }
      }

      this.state.badges = deduplicated;

      if (this.state.style !== 'badge') return;

      const availablePrimaryIndex = this.state.badges.findIndex((title) => this.primaryBadgeAvailable(title));
      if (availablePrimaryIndex > 0) {
        const [availablePrimary] = this.state.badges.splice(availablePrimaryIndex, 1);
        this.state.badges.unshift(availablePrimary);
      }

      if (availablePrimaryIndex === -1) {
        if (this.legacyVariantMode) {
          const fallback = this.badgeTitles.find((title) => this.primaryBadgeAvailable(title));
          this.state.badges = fallback ? [fallback] : [];
        } else {
          this.state.badges = [];
        }
      }

      if (this.state.badges.length > 1) {
        this.state.badges = [
          this.state.badges[0],
          ...this.state.badges.slice(1).filter((title) => Boolean(this.addonVariantFor(title)?.available)),
        ];
      }
    }

    handleChange(event) {
      const styleInput = event.target.closest('[data-xbelt-style]');
      const badgeInput = event.target.closest('[data-xbelt-badge]');

      if (!styleInput && !badgeInput) return;

      event.stopPropagation();

      if (styleInput) {
        this.state.style = styleInput.value === 'badge' ? 'badge' : 'classic';
      }

      if (badgeInput) {
        const key = this.canonical(badgeInput.value);
        this.state.badges = this.state.badges.filter((title) => this.canonical(title) !== key);
        if (badgeInput.checked) this.state.badges.push(badgeInput.value);
      }

      this.ensureValidState();
      this.saveState();
      this.syncNativeVariant();
      this.render();
    }

    handleQuantityChange() {
      this.updateTotal();
    }

    syncNativeVariant() {
      const targetValue = this.targetNativeValue(this.state.style, this.state.badges[0]);
      if (!targetValue) return false;
      return this.setNativeStyleValue(targetValue);
    }

    render() {
      this.querySelectorAll('[data-xbelt-style]').forEach((input) => {
        input.checked = input.value === this.state.style;
        input.closest('[data-xbelt-style-card]')?.classList.toggle('is-selected', input.checked);
      });

      const badgePanel = this.querySelector('[data-xbelt-badges]');
      badgePanel?.toggleAttribute('hidden', this.state.style !== 'badge');

      this.renderBadgeCards();
      this.updateStylePrices();
      this.updateTotal();
      this.updateFormProperties();
      this.manageBasePrice();
      this.updateSubmitState();
    }

    renderBadgeCards() {
      const includedKey = this.canonical(this.state.badges[0]);

      this.badgeInputs.forEach((input) => {
        const card = input.closest('[data-xbelt-badge-card]');
        const key = this.canonical(input.value);
        const selectedIndex = this.state.badges.findIndex((title) => this.canonical(title) === key);
        const selected = selectedIndex !== -1;
        const primaryAvailable = this.primaryBadgeAvailable(input.value);
        const addonVariant = this.addonVariantFor(input.value);
        const addonAvailable = Boolean(addonVariant?.available);
        const wouldBePrimary = this.state.badges.length === 0;
        const disabled = selectedIndex === 0
          ? !primaryAvailable
          : selected
            ? !addonAvailable
            : wouldBePrimary
              ? !primaryAvailable
              : !addonAvailable;

        input.checked = selected;
        input.disabled = disabled;
        card?.classList.toggle('is-selected', selected);
        card?.classList.toggle('is-disabled', disabled);
        card?.setAttribute('aria-disabled', String(disabled));

        const price = card?.querySelector('[data-xbelt-badge-price]');
        if (price) {
          price.textContent = key === includedKey ? this.dataset.includedLabel : `+${this.formatMoney(addonVariant?.price || 0)}`;
        }
      });
    }

    updateStylePrices() {
      const classicPrice = this.classicVariant()?.price;
      const badgePrice = this.badgeVariant()?.price;
      const classicTarget = this.querySelector('[data-xbelt-style-price="classic"]');
      const badgeTarget = this.querySelector('[data-xbelt-style-price="badge"]');

      if (classicTarget && Number.isFinite(classicPrice)) classicTarget.textContent = this.formatMoney(classicPrice);
      if (badgeTarget && Number.isFinite(badgePrice)) badgeTarget.textContent = this.formatMoney(badgePrice);
    }

    unitTotal() {
      const baseVariant = this.state.style === 'badge' ? this.badgeVariant() : this.classicVariant();
      let total = Number(baseVariant?.price || 0);

      if (this.state.style === 'badge') {
        this.state.badges.slice(1).forEach((title) => {
          total += Number(this.addonVariantFor(title)?.price || 0);
        });
      }

      return total;
    }

    getQuantity() {
      const quantity = Number(this.quantityInput?.value || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    updateTotal() {
      const totalTarget = this.querySelector('[data-xbelt-total]');
      if (totalTarget) totalTarget.textContent = this.formatMoney(this.unitTotal() * this.getQuantity());
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
      }
    }

    manageBasePrice() {
      this.productInfo?.querySelector('[id^="price-"]')?.classList.add('xe-xbelt-price--managed');
    }

    updateFormProperties() {
      if (!this.productForm) return;

      this.productForm.querySelectorAll('[data-xbelt-generated-property]').forEach((input) => input.remove());
      const styleLabel = this.state.style === 'badge' ? this.dataset.badgeLabel : this.dataset.classicLabel;
      this.addPropertyInput('Belt version', styleLabel);

      if (this.state.style === 'badge' && this.state.badges[0]) {
        this.addPropertyInput('Included badge', this.state.badges[0]);
        if (this.state.badges.length > 1) {
          this.addPropertyInput('Additional badges', this.state.badges.slice(1).join(', '));
        }
      }
    }

    addPropertyInput(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.xbeltGeneratedProperty = 'true';
      this.productForm.appendChild(input);
    }

    selectionIsValid() {
      if (this.state.style === 'classic') return Boolean(this.classicVariant()?.available);
      if (!this.state.badges.length) return false;
      if (!this.primaryBadgeAvailable(this.state.badges[0])) return false;
      return this.state.badges.slice(1).every((title) => Boolean(this.addonVariantFor(title)?.available));
    }

    updateSubmitState() {
      const button = this.productFormElement?.querySelector('[type="submit"]');
      const error = this.querySelector('[data-xbelt-error]');
      const valid = this.selectionIsValid();

      if (!valid) {
        if (button) {
          button.disabled = true;
          button.dataset.xbeltDisabled = 'true';
        }
        if (error) {
          error.textContent = this.dataset.requiredMessage;
          error.hidden = false;
        }
        return;
      }

      if (button?.dataset.xbeltDisabled === 'true') {
        button.removeAttribute('disabled');
        delete button.dataset.xbeltDisabled;
      }
      if (error) error.hidden = true;
    }

    buildCartItems(formData) {
      if (!this.selectionIsValid() || this.state.style !== 'badge' || this.state.badges.length < 2) return null;

      const quantity = Number(formData.get('quantity') || 1);
      const baseProperties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)]$/);
        if (match && value !== '') baseProperties[match[1]] = value;
      }

      const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      baseProperties._xbelt_bundle_id = bundleId;

      const baseItem = {
        id: Number(formData.get('id')),
        quantity,
        properties: baseProperties,
      };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) baseItem.selling_plan = Number(sellingPlan);

      const additionalItems = this.state.badges.slice(1).map((title) => {
        const variant = this.addonVariantFor(title);
        return {
          id: Number(variant.id),
          quantity,
          properties: {
            For: this.dataset.productTitle,
            _xbelt_bundle_id: bundleId,
          },
        };
      });

      return [baseItem, ...additionalItems];
    }
  }

  customElements.define('x-belt-configurator', XEngineersXbeltConfigurator);
})();
